import React from 'react'
import { MdCheckCircle, MdRadioButtonUnchecked } from 'react-icons/md'

/**
 * Live password-strength indicator. Mirrors the backend's password
 * rule (currently `min:8` in ClaimAccountRequest, RegisterRequest,
 * and Laravel's default Password::defaults()).
 *
 * Renders one row per rule with a green tick when the password
 * satisfies it, a faded outline circle when it doesn't. Keep this
 * list in lock-step with the backend rules — if either tightens, the
 * other has to follow, or customers get green ticks and still get
 * 422'd by the server.
 *
 * The component reports overall pass/fail via onValidityChange so
 * the parent can keep the submit button disabled until the rule
 * goes green.
 *
 * Strict variant (min 12 + mixedCase + numbers + symbols) was added
 * briefly and reverted; the rule list below is the cheap place to
 * reintroduce it when the business is ready.
 */

interface Props {
  password: string
  onValidityChange?: (valid: boolean) => void
}

interface Rule {
  label: string
  test: (pwd: string) => boolean
}

const RULES: Rule[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
]

function PasswordStrength({
  password,
  onValidityChange,
}: Props): React.JSX.Element {
  const results = RULES.map((rule) => ({
    ...rule,
    pass: rule.test(password),
  }))
  const allPass = results.every((r) => r.pass)

  // Notify parent (e.g. so it can enable the submit button). Effect
  // not strictly needed since results recompute every render, but
  // keeping the contract explicit helps the parent reason about it.
  React.useEffect(() => {
    onValidityChange?.(allPass)
  }, [allPass, onValidityChange])

  return (
    <ul className="password-strength" aria-label="Password requirements">
      {results.map((r) => (
        <li
          key={r.label}
          className={`password-strength-item ${r.pass ? 'is-pass' : ''}`}
        >
          <span className="password-strength-icon" aria-hidden="true">
            {r.pass ? <MdCheckCircle /> : <MdRadioButtonUnchecked />}
          </span>
          <span>{r.label}</span>
        </li>
      ))}
    </ul>
  )
}

export default PasswordStrength
